<template>
  <v-layout row wrap class="my-5 mx-0" align-start>
    <v-flex xs10 offset-xs1 md10 offset-md1 lg8 offset-lg2>
      <v-skeleton-loader
        v-if="loading"
        type="card-heading, list-item-avatar-three-line, divider, list-item-two-line"
      />

      <v-alert v-else-if="error" type="error" outlined>
        {{ error }}
      </v-alert>

      <v-card v-else-if="driver">
        <v-card-text class="pa-6">
          <v-row align="center">
            <v-col cols="12" sm="4" class="text-center">
              <v-avatar size="144" color="blue-grey darken-2">
                <v-img
                  v-if="avatarUrl"
                  :src="avatarUrl"
                  :alt="`Avatar ${fullName}`"
                />
                <span v-else class="white--text text-h3">{{ initials }}</span>
              </v-avatar>
            </v-col>

            <v-col cols="12" sm="8" :class="{'text-center': $vuetify.breakpoint.xsOnly}">
              <h1 class="text-h3 mb-3">{{ fullName }}</h1>

              <div v-if="location" class="text-subtitle-1 mb-2">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                {{ location }}
              </div>

              <v-chip v-if="profile.shorthand" class="mr-2 mt-2" outlined>
                <v-icon left small>mdi-card-account-details-outline</v-icon>
                {{ profile.shorthand }}
              </v-chip>

              <v-chip v-if="profile.discordTag" class="mr-2 mt-2" outlined>
                <v-icon left small class="fab fa-discord"></v-icon>
                {{ profile.discordTag }}
              </v-chip>

              <v-chip v-if="profile.raceNumber !== undefined && profile.raceNumber !== null" class="mt-2" outlined>
                <v-icon left small>mdi-pound</v-icon>
                {{ profile.raceNumber }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-text class="pa-6">
          <h2 class="text-h5 mb-1">RLR Rating</h2>
          <p class="text--secondary mb-4">
            Rating de performanță calculat separat pentru fiecare simulator.
          </p>

          <template v-if="rlrRatings.length">
            <v-row dense>
              <v-col v-for="rating in rlrRatings" :key="rating.simulator._id" cols="12" sm="6" md="4">
                <v-card outlined height="100%">
                  <v-card-title class="pb-1">
                    <v-icon left>mdi-steering</v-icon>
                    {{ rating.simulator.acronym || rating.simulator.title }}
                  </v-card-title>
                  <v-card-text>
                    <div class="text-h3 font-weight-bold primary--text">{{ rating.rating }}</div>
                    <div class="text-caption text--secondary mt-1">
                      {{ rating.ratedSessions }}
                      {{ rating.ratedSessions === 1 ? 'cursă clasată' : 'curse clasate' }}
                    </div>
                    <rlr-provisional-chip
                      v-if="rating.provisional"
                      :rated-sessions="rating.ratedSessions"
                      class="mt-2"
                    />
                  </v-card-text>
                  <v-card-actions>
                    <v-btn
                      :to="leaderboardRoute(rating.simulator._id)"
                      text
                      color="primary"
                    >
                      Vezi clasamentul
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
            <rlr-rating-history
              :history="rlrRatingHistory"
              :initial-simulator-id="$route.query.simulator || ''"
            />
          </template>
          <p v-else class="text--secondary mb-0">
            Pilotul nu are încă rezultate eligibile pentru RLR Rating.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-1">
            <h2 class="text-h5">Rivali Principali</h2>
            <rivalry-help-dialog class="ml-1" />
          </div>
          <p class="text--secondary mb-4">
            Piloți cu rezultate și ritm apropiat, calculați live la fiecare etapă.
          </p>

          <template v-if="rivalryGroups.length">
            <div
              v-for="(group, groupIndex) in rivalryGroups"
              :key="group.simulator._id"
              :class="{'mb-6': groupIndex < rivalryGroups.length - 1}"
            >
              <v-chip class="mb-3" outlined>
                <v-icon left small>mdi-steering</v-icon>
                {{ group.simulator.acronym || group.simulator.title }}
              </v-chip>

              <v-row dense>
                <v-col
                  v-for="(rival, rivalIndex) in group.rivals"
                  :key="rival.driver._id"
                  cols="12"
                  md="4"
                >
                  <v-card outlined height="100%">
                    <v-list-item>
                      <v-list-item-avatar color="blue-grey darken-2">
                        <v-img
                          v-if="rivalAvatarUrl(rival)"
                          :src="rivalAvatarUrl(rival)"
                          :alt="`Avatar ${rivalName(rival)}`"
                        />
                        <span v-else class="white--text">{{ rivalInitials(rival) }}</span>
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title>
                          <router-link :to="`/driver-profile/${rival.driver._id}`">
                            {{ rivalName(rival) }}
                          </router-link>
                        </v-list-item-title>
                        <v-list-item-subtitle class="d-flex align-center">
                          <v-chip
                            x-small
                            label
                            outlined
                            class="mr-2"
                            :aria-label="`Rivalul numărul ${rivalIndex + 1}`"
                          >
                            Rival #{{ rivalIndex + 1 }}
                          </v-chip>
                          {{ rivalryConfidenceLabel(rival.confidence) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                    </v-list-item>

                    <v-list dense class="pt-0">
                      <v-list-item
                        v-for="(reason, reasonIndex) in contributingRivalryReasons(rival)"
                        :key="`${reason.type}-${reasonIndex}`"
                      >
                        <v-list-item-icon class="mr-3">
                          <v-icon small>{{ rivalryReasonIcon(reason.type) }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <div class="text-overline rivalry-metric-label">
                            {{ rivalryReasonLabel(reason.type) }}
                          </div>
                          <v-list-item-title class="text-body-2 text-wrap">
                            <template v-if="reason.type === 'upcomingEvent'">
                              Ambii confirmați la
                              <router-link :to="`/event/${reason.event._id}`">
                                {{ reason.event.title }}
                              </router-link>
                            </template>
                            <template v-else>
                              {{ rivalryReasonText(reason, rival) }}
                              <v-tooltip
                                v-if="rivalryReasonTooltip(reason)"
                                bottom
                                max-width="360"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon
                                    x-small
                                    class="ml-1"
                                    :aria-label="`Explicație ${rivalryReasonLabel(reason.type).toLowerCase()}`"
                                    v-bind="attrs"
                                    v-on="on"
                                  >mdi-information-outline</v-icon>
                                </template>
                                <span>{{ rivalryReasonTooltip(reason) }}</span>
                              </v-tooltip>
                            </template>
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-if="rivalryReasonDetail(reason, rival)"
                            class="text-wrap"
                          >
                            {{ rivalryReasonDetail(reason, rival) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>

                    <details
                      v-if="controlRivalryReasons(rival).length"
                      class="rivalry-control mx-4 mb-3"
                    >
                      <summary class="grey--text text--darken-1 text-caption py-2">
                        Date de control beta ({{ controlRivalryReasons(rival).length }})
                      </summary>
                      <v-list dense class="py-0">
                        <v-list-item
                          v-for="(reason, reasonIndex) in controlRivalryReasons(rival)"
                          :key="`control-${reason.type}-${reasonIndex}`"
                          class="grey--text text--darken-1"
                        >
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="grey">{{ rivalryReasonIcon(reason.type) }}</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <div class="text-overline rivalry-metric-label grey--text text--darken-1">
                              {{ rivalryReasonLabel(reason.type) }}
                            </div>
                            <v-list-item-title class="text-body-2 text-wrap grey--text text--darken-1">
                              {{ rivalryReasonText(reason, rival) }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-wrap grey--text">
                              {{ rivalryReasonDetail(reason, rival) }} · Nu contribuie la scorul actual
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </details>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </template>

          <p v-else class="text--secondary mb-0">
            Nu există încă suficiente date pentru identificarea rivalilor principali.
          </p>
        </v-card-text>

        <v-divider />

        <v-card-text class="pa-6">
          <h2 class="text-h5 mb-4">Cluburi</h2>

          <v-row v-if="driver.teams.length">
            <v-col v-for="team in driver.teams" :key="team._id" cols="12" sm="6">
              <v-card outlined height="100%">
                <v-list-item three-line>
                  <v-list-item-avatar tile size="56" color="blue-grey lighten-4">
                    <v-img v-if="teamLogoUrl(team)" :src="teamLogoUrl(team)" :alt="team.name" />
                    <v-icon v-else>mdi-account-group</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="text-h6">{{ team.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="team.simulator">
                      {{ team.simulator.title }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-col>
          </v-row>

          <p v-else class="text--secondary mb-0">Pilotul nu face parte din niciun club.</p>
        </v-card-text>

        <v-divider />

        <v-card-text class="pa-6">
          <h2 class="text-h5 mb-4">Palmares</h2>

          <template v-if="groupedPerformanceHistory.length">
            <v-simple-table class="d-none d-sm-block">
              <thead>
                <tr>
                  <th>An</th>
                  <th>Sim.</th>
                  <th>Sezon</th>
                  <th>Clasări</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="seasonResult in groupedPerformanceHistory" :key="seasonResult.season._id">
                  <td>{{ formatSeasonYear(seasonResult.season) }}</td>
                  <td>
                    <v-chip
                      v-if="seasonResult.season.simulator"
                      small
                      outlined
                      :title="seasonResult.season.simulator.title"
                      :aria-label="seasonResult.season.simulator.title"
                    >
                      <v-icon left x-small>mdi-steering</v-icon>
                      {{ seasonResult.season.simulator.acronym || seasonResult.season.simulator.title }}
                    </v-chip>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <router-link :to="`/season/${seasonResult.season._id}?tab=standings`">
                      {{ seasonResult.season.title }}
                    </router-link>
                    <v-icon
                      v-if="seasonResult.sharedCar"
                      small
                      class="ml-1"
                      title="Sezon cu mașină împărțită între mai mulți piloți"
                      aria-label="Sezon cu mașină împărțită între mai mulți piloți"
                    >mdi-account-multiple</v-icon>
                  </td>
                  <td>
                    <v-chip
                      v-for="result in seasonResult.results"
                      :key="result.standings._id"
                      class="mr-2 my-1"
                      :class="podiumClass(result)"
                      :color="resultColor(result)"
                      small
                      :outlined="!isPodium(result)"
                    >
                      <span class="font-weight-medium">{{ result.standings.title.toUpperCase() }}</span>
                      <strong class="ml-2">{{ result.disqualified ? 'DSQ' : formatPosition(result.position) }}</strong>
                      <span class="ml-1 text--secondary">/ {{ result.totalDrivers }}</span>
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div class="d-sm-none">
              <div
                v-for="(seasonResult, index) in groupedPerformanceHistory"
                :key="seasonResult.season._id"
                class="py-3"
              >
                <div class="d-flex align-center mb-2">
                  <v-chip x-small label outlined class="mr-2">
                    {{ formatSeasonYear(seasonResult.season) }}
                  </v-chip>
                  <v-chip
                    v-if="seasonResult.season.simulator"
                    x-small
                    outlined
                    :title="seasonResult.season.simulator.title"
                    :aria-label="seasonResult.season.simulator.title"
                  >
                    <v-icon left x-small>mdi-steering</v-icon>
                    {{ seasonResult.season.simulator.acronym || seasonResult.season.simulator.title }}
                  </v-chip>
                </div>

                <router-link
                  class="d-block font-weight-medium mb-2"
                  :to="`/season/${seasonResult.season._id}?tab=standings`"
                >
                  {{ seasonResult.season.title }}
                  <v-icon
                    v-if="seasonResult.sharedCar"
                    small
                    class="ml-1"
                    title="Sezon cu mașină împărțită între mai mulți piloți"
                    aria-label="Sezon cu mașină împărțită între mai mulți piloți"
                  >mdi-account-multiple</v-icon>
                </router-link>

                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="result in seasonResult.results"
                    :key="result.standings._id"
                    class="mr-2 mb-2"
                    :class="podiumClass(result)"
                    :color="resultColor(result)"
                    small
                    :outlined="!isPodium(result)"
                  >
                    <span class="font-weight-medium">{{ result.standings.title.toUpperCase() }}</span>
                    <strong class="ml-2">{{ result.disqualified ? 'DSQ' : formatPosition(result.position) }}</strong>
                    <span class="ml-1 text--secondary">/ {{ result.totalDrivers }}</span>
                  </v-chip>
                </div>

                <v-divider v-if="index < groupedPerformanceHistory.length - 1" />
              </div>
            </div>
          </template>

          <p v-else class="text--secondary mb-0">Nu există încă rezultate publice în clasamente.</p>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import axios from '@/plugins/axios'
import countryData from '@/plugins/vuetify-country-region-input/data'
import RivalryHelpDialog from '@/components/RivalryHelpDialog.vue'
import RlrProvisionalChip from '@/components/RlrProvisionalChip.vue'
import RlrRatingHistory from '@/components/RlrRatingHistory.vue'

export default {
  pageTitle: 'Driver Profile',
  components: {
    RivalryHelpDialog,
    RlrProvisionalChip,
    RlrRatingHistory
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      driver: null,
      loading: true,
      error: ''
    }
  },
  computed: {
    profile() {
      return this.driver && this.driver.profile ? this.driver.profile : {}
    },
    fullName() {
      const name = [this.profile.firstName, this.profile.lastName].filter(Boolean).join(' ')
      return name || 'Pilot RLR'
    },
    initials() {
      return [this.profile.firstName, this.profile.lastName]
        .filter(Boolean)
        .map(name => name.charAt(0).toUpperCase())
        .join('') || 'RLR'
    },
    avatarUrl() {
      if (!this.profile.profilePicture || !this.profile.profilePicture.filename) {
        return ''
      }
      return `${process.env.VUE_APP_API_SCHEMA}${process.env.VUE_APP_API_HOST}/api/files/${this.profile.profilePicture.filename}`
    },
    location() {
      if (!this.profile.country) {
        return ''
      }

      const country = countryData.find(item => item.countryShortCode === this.profile.country)
      if (!country) {
        return [this.profile.city, this.profile.country].filter(Boolean).join(', ')
      }

      const region = country.regions.find(item => item.shortCode === this.profile.city)
      return [region ? region.name : this.profile.city, country.countryName].filter(Boolean).join(', ')
    },
    groupedPerformanceHistory() {
      const groups = []
      const groupBySeasonId = {}
      const history = this.driver && this.driver.performanceHistory ? this.driver.performanceHistory : []

      history.forEach(result => {
        const seasonId = result.season._id
        if (!groupBySeasonId[seasonId]) {
          groupBySeasonId[seasonId] = {
            season: result.season,
            sharedCar: false,
            results: []
          }
          groups.push(groupBySeasonId[seasonId])
        }

        groupBySeasonId[seasonId].results.push(result)
        groupBySeasonId[seasonId].sharedCar = groupBySeasonId[seasonId].sharedCar || result.sharedCar
      })

      return groups
    },
    rivalryGroups() {
      return this.driver && this.driver.rivalries ? this.driver.rivalries : []
    },
    rlrRatings() {
      const ratings = this.driver && this.driver.rlrRatings ? this.driver.rlrRatings : []
      return ratings.slice().sort((first, second) => {
        const order = this.simulatorOrder(first.simulator) - this.simulatorOrder(second.simulator)
        return order || first.simulator.title.localeCompare(second.simulator.title)
      })
    },
    rlrRatingHistory() {
      return this.driver && this.driver.rlrRatingHistory ? this.driver.rlrRatingHistory : []
    }
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.loadDriver()
      }
    }
  },
  methods: {
    simulatorOrder(simulator) {
      const key = (simulator.acronym || simulator.title || '').toLowerCase()
      if (key === 'ir' || key === 'iracing') return 0
      if (key === 'ac') return 1
      if (key === 'acc') return 2
      return 3
    },
    leaderboardRoute(simulatorId) {
      return {
        path: '/rlr-rating',
        query: simulatorId ? {simulator: simulatorId} : {}
      }
    },
    teamLogoUrl(team) {
      if (team.logo && team.logo.filename) {
        return `${process.env.VUE_APP_API_SCHEMA}${process.env.VUE_APP_API_HOST}/api/files/${team.logo.filename}`
      }
      return team.image || ''
    },
    resultColor(result) {
      if (result.disqualified) {
        return 'error'
      }

      const podiumColors = {
        1: '#d4af37',
        2: '#c0c0c0',
        3: '#ceba9b'
      }

      return podiumColors[result.position]
    },
    isPodium(result) {
      return !result.disqualified && result.position >= 1 && result.position <= 3
    },
    podiumClass(result) {
      if (!this.isPodium(result)) {
        return ''
      }

      return {
        1: 'palmares-result--gold',
        2: 'palmares-result--silver',
        3: 'palmares-result--bronze'
      }[result.position]
    },
    formatSeasonYear(season) {
      const date = season.timeStart || season.timeEnd
      return date ? new Date(date).getFullYear() : '—'
    },
    formatPosition(position) {
      return position !== undefined && position !== null ? `P${position}` : '—'
    },
    rivalProfile(rival) {
      return rival && rival.driver && rival.driver.profile ? rival.driver.profile : {}
    },
    rivalName(rival) {
      const profile = this.rivalProfile(rival)
      return [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Pilot RLR'
    },
    rivalInitials(rival) {
      const profile = this.rivalProfile(rival)
      return [profile.firstName, profile.lastName]
        .filter(Boolean)
        .map(name => name.charAt(0).toUpperCase())
        .join('') || 'RLR'
    },
    rivalAvatarUrl(rival) {
      const picture = this.rivalProfile(rival).profilePicture
      if (!picture || !picture.filename) {
        return ''
      }

      return `${process.env.VUE_APP_API_SCHEMA}${process.env.VUE_APP_API_HOST}/api/files/${picture.filename}`
    },
    rivalryConfidenceLabel(confidence) {
      const labels = {
        high: 'Rivalitate actuală',
        tentative: 'Rivalitate posibilă',
        event: 'Pentru etapa următoare'
      }
      return labels[confidence] || 'Rivalitate posibilă'
    },
    sortedRivalryReasons(rival) {
      const reasons = rival.reasons || []
      const priority = rival.confidence === 'event'
        ? ['upcomingEvent', 'pace', 'standings', 'closeEvents', 'headToHead']
        : ['upcomingEvent', 'closeEvents', 'standings', 'pace', 'headToHead']

      return reasons.slice().sort((a, b) => {
        return priority.indexOf(a.type) - priority.indexOf(b.type)
      })
    },
    contributingRivalryReasons(rival) {
      return this.sortedRivalryReasons(rival).filter(reason => reason.contributes !== false)
    },
    controlRivalryReasons(rival) {
      return this.sortedRivalryReasons(rival).filter(reason => reason.contributes === false)
    },
    rivalryReasonIcon(type) {
      const icons = {
        upcomingEvent: 'mdi-calendar-check',
        closeEvents: 'mdi-flag-checkered',
        standings: 'mdi-format-list-numbered',
        pace: 'mdi-timer-outline',
        headToHead: 'mdi-sword-cross'
      }
      return icons[type] || 'mdi-information-outline'
    },
    rivalryReasonLabel(type) {
      const labels = {
        upcomingEvent: 'Etapa următoare',
        closeEvents: 'Curse',
        standings: 'Clasament',
        pace: 'Ritm',
        headToHead: 'Direct'
      }
      return labels[type] || 'Detalii'
    },
    rivalryReasonText(reason, rival) {
      if (reason.type === 'closeEvents') {
        const totalEvents = reason.historyEventCount || 6
        return `${reason.eventCount}/${totalEvents} etape apropiate`
      }

      if (reason.type === 'standings') {
        const verb = reason.completed ? 'a fost' : 'este'
        if (reason.signedPointsGap > 0) {
          return `${this.rivalName(rival)} ${verb} cu ${this.formatPoints(reason.pointsGap)} mai sus`
        }
        if (reason.signedPointsGap < 0) {
          return `${this.fullName} ${verb} cu ${this.formatPoints(reason.pointsGap)} mai sus`
        }
        return 'Egalitate de puncte'
      }

      if (reason.type === 'pace') {
        const sampleLabel = reason.source === 'prequalification'
          ? 'precalificare'
          : (reason.sampleCount === 1 ? 'calificare' : 'calificări')
        return `${reason.sampleCount} ${sampleLabel} · indice ${this.formatIndex(reason.proximityIndex)}`
      }

      if (reason.type === 'headToHead') {
        return `${this.fullName} ${reason.targetAhead} — ${reason.rivalAhead} ${this.rivalName(rival)}`
      }

      return ''
    },
    rivalryReasonDetail(reason, rival) {
      if (reason.type === 'closeEvents') {
        const positionGap = reason.maxPositionGap === 1
          ? 'o poziție'
          : `${reason.maxPositionGap} poziții`
        return `Cel puțin o cursă la maximum ${positionGap} · indice ponderat ${this.formatIndex(reason.proximityIndex)}`
      }

      if (reason.type === 'standings') {
        return `${reason.standingsTitle} · ${reason.seasonTitle}`
      }

      if (reason.type === 'pace') {
        const difference = this.formatSecondsPerMinute(Math.abs(reason.signedPercentageGap))
        const context = reason.source === 'prequalification' ? 'în precalificare' : 'în medie'
        if (Math.abs(reason.signedPercentageGap) < 0.005) {
          return `Ritm practic egal ${context}`
        }
        if (reason.signedPercentageGap < 0) {
          return `${this.rivalName(rival)} a fost ${context} cu ${difference} mai rapid`
        }
        return `${this.fullName} a fost ${context} cu ${difference} mai rapid`
      }

      return ''
    },
    rivalryReasonTooltip(reason) {
      if (reason.type === 'closeEvents') {
        return 'Indicele agregă apropierea pozițiilor finale din cursele comparabile. Etapele recente cântăresc mai mult, iar echilibrul scorului direct ajustează ușor rezultatul. 100% reprezintă apropierea maximă.'
      }

      if (reason.type === 'pace') {
        const source = reason.source === 'prequalification' ? 'precalificare' : 'calificare'
        return `Indicele agregă diferențele de ritm folosite în clasament; 100% înseamnă ritm identic. Cea mai mică diferență a fost ${this.formatSecondsPerMinute(reason.percentageGap)} în ${source}, la ${reason.eventTitle}.`
      }

      if (reason.type === 'headToHead') {
        return 'Primul număr arată cursele oficiale în care pilotul din profil a terminat înaintea rivalului; al doilea arată cursele în care rivalul a terminat înainte. Se folosesc pozițiile finale din clasă din ultimele 6 etape eligibile, iar fiecare cursă a unei etape se numără separat. Nu reprezintă victorii sau depășiri.'
      }

      return ''
    },
    formatPoints(points) {
      const value = Number(points)
      return `${Number.isInteger(value) ? value : value.toFixed(1)} ${value === 1 ? 'punct' : 'puncte'}`
    },
    formatSecondsPerMinute(percentage) {
      const seconds = Number(percentage) * 0.6
      return `${seconds.toLocaleString('ro-RO', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      })}s/min`
    },
    formatIndex(index) {
      return `${Math.round(Number(index))}%`
    },
    async loadDriver() {
      this.loading = true
      this.error = ''
      this.driver = null

      try {
        const response = await axios.get(`/api/driver/${this.id}`)
        this.driver = Object.assign({
          teams: [],
          performanceHistory: [],
          rivalries: [],
          rlrRatings: [],
          rlrRatingHistory: []
        }, response.data.data)
      } catch (error) {
        this.error = error.response && error.response.status === 404
          ? 'Pilotul nu a fost găsit.'
          : 'Profilul pilotului nu a putut fi încărcat.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.rivalry-metric-label {
  line-height: 1.2;
}

.rivalry-control {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.rivalry-control summary {
  cursor: pointer;
}

.palmares-result--gold,
.palmares-result--silver,
.palmares-result--bronze {
  color: #292929 !important;
  border: 1px solid rgba(0, 0, 0, 0.28) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 1px 2px rgba(0, 0, 0, 0.18);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
}

.palmares-result--gold {
  background: linear-gradient(135deg, #9b7209 0%, #f8e38a 24%, #d4af37 48%, #fff0a3 68%, #9b7209 100%) !important;
}

.palmares-result--silver {
  background: linear-gradient(135deg, #858a90 0%, #f4f6f7 24%, #c0c0c0 48%, #ffffff 68%, #858a90 100%) !important;
}

.palmares-result--bronze {
  background: linear-gradient(135deg, #88583a 0%, #efd5bb 24%, #ceba9b 48%, #fae7d3 68%, #88583a 100%) !important;
}
</style>
